/**
 * Canonical UK phone handling for Instacut.
 *
 * ONE source of truth used by:
 *   • PhoneInput component (input formatting + validation)
 *   • Every form that captures a phone (public booking, Diary +New,
 *     Clients +Add, ClientDetail edit)
 *   • Booking detail panel (tel: links + display)
 *   • Backend match logic (via matching normalization applied on write)
 *
 * Canonical UK mobile format is `XXXXX XXXXXX`, e.g. `07700 900123`.
 * That is the ONLY thing we store on `customers.phone` for UK numbers.
 *
 * Non-UK numbers bypass all of this — see `isValidUKMobile` +
 * the is_non_uk_number flag surfaced by <PhoneInput>.
 */

// Strip everything that isn't a digit.
const digitsOnly = (s) => String(s || '').replace(/\D+/g, '');

/**
 * Normalize a user-typed phone string to canonical UK mobile format.
 * Returns `null` if the input isn't a recognisable UK mobile.
 *
 * Accepted inputs:
 *   • "07700 900123"       → "07700 900123"
 *   • "07700900123"        → "07700 900123"
 *   • "+447700900123"      → "07700 900123"
 *   • "447700 900123"      → "07700 900123"
 *   • "0 7 7 0 0 900123"   → "07700 900123"
 * Rejected:
 *   • Anything with fewer or more digits than 11 (post-conversion)
 *   • Anything not starting with `07` (post-conversion)
 */
export const normalizeUKMobile = (input) => {
  let d = digitsOnly(input);
  // Convert +44… / 44… → 0…
  if (d.startsWith('44') && d.length === 12) d = '0' + d.slice(2);
  if (d.length !== 11) return null;
  if (!d.startsWith('07')) return null;
  return `${d.slice(0, 5)} ${d.slice(5)}`;
};

/**
 * Predicate: is this a valid UK mobile (post-normalization)?
 */
export const isValidUKMobile = (input) => normalizeUKMobile(input) !== null;

/**
 * Live-format a partial UK mobile string while the user types.
 * Preserves what's been typed so far, only inserts the single space
 * once 5+ digits are present. Also collapses a leading `+44` / `44`
 * prefix into `0` so pasting an international-format number lands as
 * `07700 900123`. Used inside <PhoneInput>.
 */
export const formatUKMobileAsYouType = (input) => {
  let d = digitsOnly(input);
  // Collapse a leading 44 country code once enough digits are present
  // to distinguish it from a genuine `44…` (which would be invalid as
  // a UK mobile anyway — UK mobiles start with `07`).
  if (d.startsWith('44') && d.length >= 12) d = '0' + d.slice(2);
  d = d.slice(0, 11); // max 11 digits for a UK mobile
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)} ${d.slice(5)}`;
};

/**
 * Format a stored value for display. Falls back to the raw value if
 * it can't be normalised (e.g. a non-UK number stored as-typed).
 */
export const formatPhoneForDisplay = (stored) => {
  if (!stored) return '';
  return normalizeUKMobile(stored) || String(stored);
};

/**
 * Strip a phone for the `href="tel:..."` attribute — no spaces at
 * all. Works for both UK and non-UK stored values.
 */
export const stripPhoneForTel = (stored) => digitsOnly(stored);

/**
 * The "match key" used to detect duplicates. Digits-only, no country
 * code. Same rule applied on FE and BE so the app-level check and the
 * DB unique index always agree.
 */
export const phoneMatchKey = (input) => {
  let d = digitsOnly(input);
  if (d.startsWith('44') && d.length === 12) d = '0' + d.slice(2);
  return d;
};
